import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { CreditCard, AlertTriangle, Check, Users, Calendar, Mail, Sparkles, FileText, Download, Receipt } from "lucide-react";

const PLAN_LABELS = {
  trial:      { name: "Trial",      seats: 5,   color: "bg-gray-100 text-gray-700",     tagline: "Prova gratuita di tutte le funzioni" },
  starter:    { name: "Starter",    seats: 10,  color: "bg-[#FFF0E3] text-[#E85D00]",   tagline: "Per team piccoli che iniziano" },
  business:   { name: "Business",   seats: 50,  color: "bg-[#FFE4CC] text-[#E85D00]",   tagline: "Per PMI in crescita" },
  enterprise: { name: "Enterprise", seats: 500, color: "bg-[#0A0A0A] text-[#FF7A29]",   tagline: "Per aziende strutturate" },
};

const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "—";

export default function BillingPage() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
    api.get("/subscription/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  }, []);

  const downloadPdf = async (inv) => {
    try {
      const token = localStorage.getItem("tb_token");
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${apiUrl}/api/subscription/invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("download failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Fattura_${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch {
      window.alert("Errore durante il download. Riprova.");
    }
  };

  const plan = sub?.plan || user?.plan || "trial";
  const info = PLAN_LABELS[plan] || PLAN_LABELS.trial;
  const warn = sub && sub.days_remaining !== null && sub.days_remaining <= 7 && !sub.is_expired;
  const seatsPct = sub ? Math.min(100, (sub.seats_used / Math.max(1, sub.seats_limit)) * 100) : 0;
  const nearLimit = seatsPct >= 70;

  return (
    <div className="p-4 md:p-12" data-testid="billing-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Abbonamento</div>
        <h1 className="font-display text-4xl">Il tuo piano</h1>
        <p className="text-[#555] mt-1 text-sm">Dettagli abbonamento in essere e storico fatture</p>
      </div>

      {/* =========== PIANO ATTUALE =========== */}
      <div className="card-smart overflow-hidden mb-8" data-testid="current-plan-card">
        <div className={`p-8 md:p-10 ${plan === "enterprise" ? "bg-[#0A0A0A] text-white" : "bg-gradient-to-br from-[#FFFAF5] to-[#FFF0E3]"}`}>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full ${info.color}`}>
                <Sparkles className="w-3 h-3"/> Piano attuale
              </div>
              <h2 className="font-display text-6xl mt-4" data-testid="plan-name">{info.name}</h2>
              <p className={`text-sm mt-2 ${plan === "enterprise" ? "text-white/60" : "text-[#757575]"}`}>{info.tagline}</p>
            </div>
            <div className="text-right">
              <div className={`text-[10px] uppercase tracking-widest ${plan === "enterprise" ? "text-white/60" : "text-[#757575]"}`}>Fatturazione</div>
              <div className="font-semibold mt-1 text-lg">{sub?.interval === "yearly" ? "Annuale (-10%)" : "Mensile"}</div>
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {/* Stato */}
            <div className={`rounded-2xl p-5 ${plan === "enterprise" ? "bg-white/10" : "bg-white border border-[#EDE5D8]"}`} data-testid="sub-status">
              <div className="flex items-start justify-between mb-2">
                <div className={`text-[10px] uppercase tracking-widest ${plan === "enterprise" ? "text-white/60" : "text-[#757575]"}`}>Stato</div>
                {sub?.is_expired ? <AlertTriangle className="w-4 h-4 text-red-500"/> : warn ? <AlertTriangle className="w-4 h-4 text-amber-500"/> : <Check className="w-4 h-4 text-green-500"/>}
              </div>
              <div className="font-display text-2xl">
                {sub?.is_expired ? "Scaduto" : warn ? "In scadenza" : plan === "trial" ? "Attivo (Trial)" : "Attivo"}
              </div>
              {!sub?.is_expired && warn && <div className="text-xs text-amber-700 font-semibold mt-1">⏰ Scade tra {sub.days_remaining} giorni</div>}
            </div>

            {/* Scadenza */}
            <div className={`rounded-2xl p-5 ${plan === "enterprise" ? "bg-white/10" : "bg-white border border-[#EDE5D8]"}`} data-testid="sub-expiry">
              <div className="flex items-start justify-between mb-2">
                <div className={`text-[10px] uppercase tracking-widest ${plan === "enterprise" ? "text-white/60" : "text-[#757575]"}`}>Prossimo rinnovo</div>
                <Calendar className={`w-4 h-4 ${plan === "enterprise" ? "text-[#FF7A29]" : "text-[#FF7A29]"}`}/>
              </div>
              <div className="font-display text-2xl">{sub?.expires_at ? formatDate(sub.expires_at) : "—"}</div>
              <div className={`text-xs mt-1 ${plan === "enterprise" ? "text-white/60" : "text-[#757575]"}`}>
                {sub?.days_remaining !== null && sub?.days_remaining !== undefined ? `${sub.days_remaining} giorni rimanenti` : "Piano trial senza scadenza"}
              </div>
            </div>

            {/* Seats */}
            <div className={`rounded-2xl p-5 ${plan === "enterprise" ? "bg-white/10" : "bg-white border border-[#EDE5D8]"}`} data-testid="sub-seats">
              <div className="flex items-start justify-between mb-2">
                <div className={`text-[10px] uppercase tracking-widest ${plan === "enterprise" ? "text-white/60" : "text-[#757575]"}`}>Dipendenti</div>
                <Users className="w-4 h-4 text-[#FF7A29]"/>
              </div>
              <div className="font-display text-2xl">{sub?.seats_used ?? 0} <span className={`text-base ${plan === "enterprise" ? "text-white/50" : "text-[#757575]"}`}>/ {sub?.seats_limit ?? info.seats}</span></div>
              <div className={`mt-2 h-2 rounded-full overflow-hidden ${plan === "enterprise" ? "bg-white/20" : "bg-[#EDE5D8]"}`}>
                <div className={`h-full rounded-full transition-all ${seatsPct >= 90 ? "bg-red-500" : seatsPct >= 70 ? "bg-amber-500" : "bg-[#FF7A29]"}`} style={{width: `${seatsPct}%`}}/>
              </div>
              <div className={`text-xs mt-1 ${plan === "enterprise" ? "text-white/60" : "text-[#757575]"}`}>
                {(sub?.seats_available ?? 0) > 0 ? `${sub.seats_available} disponibili` : "⚠️ Limite raggiunto"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =========== BANNER UPGRADE =========== */}
      {(nearLimit || sub?.is_expired || warn || plan === "trial") && (
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#FF7A29] to-[#E85D00] text-white p-8 md:p-10 mb-8 shadow-xl shadow-orange-200" data-testid="upgrade-banner">
          <div className="absolute -right-12 -top-12 w-64 h-64 bg-white/5 rounded-full blur-2xl"/>
          <div className="absolute -right-8 -bottom-8 w-40 h-40 bg-white/10 rounded-full blur-xl"/>
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-7 h-7"/>
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest opacity-80 mb-1">Upgrade disponibile</div>
              <h3 className="font-display text-3xl mb-2">Hai bisogno di un upgrade?</h3>
              <p className="text-base opacity-90 mb-4 max-w-xl">La tua azienda è cresciuta — contattaci per trovare il piano su misura per le tue nuove esigenze.</p>
              <a href="mailto:info@toolbadge.it?subject=Richiesta%20upgrade%20Tool%20Badge" data-testid="upgrade-contact-btn" className="inline-flex items-center gap-2 bg-white text-[#FF7A29] font-semibold px-6 py-3 rounded-full hover:bg-[#FFFAF5] transition-all shadow-lg">
                <Mail className="w-4 h-4"/> info@toolbadge.it
              </a>
            </div>
          </div>
        </div>
      )}

      {/* =========== FATTURE PAGATE =========== */}
      <div className="card-smart overflow-hidden" data-testid="invoices-section">
        <div className="px-6 py-5 border-b border-[#EDE5D8] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FFF0E3] flex items-center justify-center">
              <Receipt className="w-5 h-5 text-[#FF7A29]"/>
            </div>
            <div>
              <h3 className="font-display text-xl">Fatture pagate</h3>
              <p className="text-xs text-[#757575]">Storico abbonamento in essere</p>
            </div>
          </div>
          <span className="text-xs text-[#757575]">{invoices.length} {invoices.length === 1 ? "fattura" : "fatture"}</span>
        </div>

        {invoices.length === 0 ? (
          <div className="p-16 text-center">
            <FileText className="w-12 h-12 mx-auto mb-3 text-[#FF7A29]/40"/>
            <div className="text-[#757575] text-sm mb-1">Nessuna fattura disponibile</div>
            <div className="text-xs text-[#999]">Le fatture appariranno qui dopo il primo pagamento</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-[#EDE5D8] bg-[#FFFAF5]">
              <tr className="text-left">
                <th className="p-4 font-semibold">Numero</th>
                <th className="p-4 font-semibold">Data</th>
                <th className="p-4 font-semibold">Piano</th>
                <th className="p-4 font-semibold">Fatturazione</th>
                <th className="p-4 font-semibold text-right">Importo</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-[#EDE5D8] hover:bg-[#FFF0E3]/30 transition-colors" data-testid={`invoice-${inv.id}`}>
                  <td className="p-4 font-mono text-xs font-bold">{inv.invoice_number}</td>
                  <td className="p-4 text-xs text-[#757575]">{formatDate(inv.created_at)}</td>
                  <td className="p-4 font-semibold">{inv.plan_name}</td>
                  <td className="p-4 text-xs">
                    <span className="bg-[#FFF0E3] text-[#E85D00] px-2 py-1 rounded-full font-semibold uppercase">
                      {inv.plan_interval === "yearly" ? "Annuale" : "Mensile"}
                    </span>
                  </td>
                  <td className="p-4 text-right font-display text-lg">€{inv.amount?.toFixed(2)}</td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className="inline-flex items-center gap-1 text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-full font-bold uppercase tracking-widest">
                        <Check className="w-3 h-3"/> Pagata
                      </span>
                      <Button size="icon" variant="ghost" onClick={()=>downloadPdf(inv)} data-testid={`download-invoice-${inv.id}`} title="Scarica PDF" className="hover:bg-[#FFF0E3] rounded-full h-8 w-8">
                        <Download className="w-4 h-4 text-[#FF7A29]"/>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer annuale tip */}
      {sub?.interval !== "yearly" && plan !== "trial" && (
        <div className="mt-6 text-center text-sm text-[#757575]" data-testid="yearly-tip">
          💡 Passando al piano <strong>annuale</strong> risparmi il <strong>10%</strong> — scrivi a <a href="mailto:info@toolbadge.it" className="text-[#FF7A29] underline">info@toolbadge.it</a>
        </div>
      )}
    </div>
  );
}
